const DEFAULT_QUALITY = 90;
const DEFAULT_FORMAT = 'avif';
const DEFAULT_DENSITY = 2;

const EXCLUDED_HOSTS = ['sanity.io', 'cdn.sanity.io', 'cdninstagram.com'];

export function isShopwareImageUrl(url: string | undefined | null): boolean {
    if (!url || url.startsWith('/')) {
        return false;
    }

    try {
        const { hostname, protocol } = new URL(url);
        if (protocol !== 'http:' && protocol !== 'https:') {
            return false;
        }

        return !EXCLUDED_HOSTS.some(host => hostname === host || hostname.endsWith(`.${host}`));
    } catch {
        return false;
    }
}

interface ShopwareImageUrlOptions {
    /** Layout width in CSS pixels. CDN width is derived from this value. */
    width?: number;
    quality?: number;
    format?: string;
    /** Multiplier applied to width for CDN requests (defaults to 2 for retina). */
    density?: number;
}

function resolveCdnWidth(width: number, density = DEFAULT_DENSITY): number {
    return Math.round(width * density);
}

export function useShopwareImageUrl(
    url: string | undefined | null,
    options: ShopwareImageUrlOptions = {},
): string {
    if (!url) {
        return '';
    }

    if (!isShopwareImageUrl(url)) {
        return url;
    }

    const [base, query = ''] = url.split('?');
    const params = new URLSearchParams(query);

    if (options.width != null) {
        params.set('width', String(resolveCdnWidth(options.width, options.density ?? DEFAULT_DENSITY)));
    }

    params.set('quality', String(options.quality ?? DEFAULT_QUALITY));
    params.set('format', options.format ?? DEFAULT_FORMAT);

    const queryString = params.toString();
    return queryString ? `${base}?${queryString}` : base;
}

export function useShopwareImageSrcSet(
    url: string | undefined | null,
    options: Omit<ShopwareImageUrlOptions, 'density'> & { densities?: number[] },
): { src: string; srcset: string } {
    const densities = options.densities ?? [1, 2];
    const { densities: _, ...urlOptions } = options;

    return {
        src: useShopwareImageUrl(url, { ...urlOptions, density: densities[0] }),
        srcset: densities
            .map(density => `${useShopwareImageUrl(url, { ...urlOptions, density })} ${density}x`)
            .join(', '),
    };
}
